import { useMemo } from 'react'
import { Crosshair, Gauge, Move3D, Navigation, Radar } from 'lucide-react'
import { Button, EmptyHint, Metric } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { bearingDeg, distanceM, formatLatLon } from '../../lib/geo'
import { fmtAlt, fmtBearing, fmtDistance, fmtSpeed } from '../../lib/format'
import type { TrafficTrack } from '../../lib/trackModel'

/* ============================================================================
 * ИНСТРУМЕНТ «ДАЛЬНОСТЬ И АЗИМУТ» (RANGE & BEARING)
 *
 * Оператор фиксирует «СВОЙ БВС» (ownship lock) и получает по выбранной цели:
 *   • дальность (slant range), м;
 *   • азимут истинный, градусы;
 *   • скорость цели, м/с;
 *   • высота цели, м;
 *   • взаимное превышение (цель минус свой БВС), м;
 *   • сближение — знак вертикальной скорости сближения.
 *
 * Все углы и длины — WGS-84.
 * ========================================================================= */

export function RangeBearing({
  ownship,
  target,
  tracks,
  onLock,
  onUnlock,
  onSelectTarget,
}: {
  ownship: TrafficTrack | null
  target: TrafficTrack | null
  tracks: TrafficTrack[]
  onLock: (t: TrafficTrack) => void
  onUnlock: () => void
  onSelectTarget: (id: string) => void
}) {
  // Расчёт дистанции, азимута и сближения — только при наличии обеих точек
  const solution = useMemo(() => {
    if (!ownship || !target) return null
    if (ownship.lat === null || ownship.lon === null) return null
    if (target.lat === null || target.lon === null) return null

    const range = distanceM(
      { lat: ownship.lat, lon: ownship.lon },
      { lat: target.lat, lon: target.lon },
    )
    const brg = bearingDeg(
      { lat: ownship.lat, lon: ownship.lon },
      { lat: target.lat, lon: target.lon },
    )
    // Разница высот: положительная — цель выше
    const altDiff =
      ownship.altM !== null && target.altM !== null ? target.altM - ownship.altM : null
    // Наклонная дальность с учётом вертикали
    const slant = altDiff !== null ? Math.sqrt(range * range + altDiff * altDiff) : range

    return { range, brg, altDiff, slant }
  }, [ownship, target])

  if (!ownship) {
    return (
      <div className="space-y-2 p-2.5">
        <div className="ut-stated ut-state-pending rounded-md border px-2.5 py-2 text-[11px]">
          <div className="flex items-center gap-1.5 font-semibold">
            <Crosshair size={12} aria-hidden />
            «Свой БВС» не зафиксирован
          </div>
          <div className="mt-0.5 text-ink-muted">
            Выберите цель из списка и нажмите «Зафиксировать свой БВС». После этого
            инструмент рассчитает дальность, азимут и превышение по любой цели.
          </div>
        </div>
        {tracks.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
              Доступные цели
            </div>
            {tracks.slice(0, 8).map((t) => (
              <button
                key={t.id}
                className="ut-btn ut-btn-sm w-full justify-between"
                onClick={() => onLock(t)}
              >
                <span>{t.callsign}</span>
                <span className="ut-metric text-ink-muted">{fmtAlt(t.altM)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2 p-2.5">
      {/* Зафиксированный «свой БВС» */}
      <div className="ut-stated ut-state-selected rounded-md border px-2.5 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
            <Crosshair size={12} aria-hidden />
            Свой БВС: {ownship.callsign}
          </div>
          <StateBadge state="selected">Зафиксирован</StateBadge>
        </div>
        <div className="ut-metric mt-1 grid grid-cols-2 gap-x-2 text-[10px] text-ink-muted">
          <span>Позывной: {ownship.callsign}</span>
          <span>Высота: {fmtAlt(ownship.altM)}</span>
          <span>Скорость: {fmtSpeed(ownship.speedMs)}</span>
          <span>Курс: {fmtBearing(ownship.headingDeg)}</span>
          <span className="col-span-2">
            {ownship.lat !== null && ownship.lon !== null
              ? formatLatLon(ownship.lat, ownship.lon)
              : '—'}
          </span>
        </div>
        <Button size="sm" onClick={onUnlock} className="mt-1.5" full>
          Снять фиксацию
        </Button>
      </div>

      {/* Выбор цели */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
          Цель
        </div>
        <select
          className="ut-select mt-1"
          value={target?.id ?? ''}
          onChange={(e) => {
            const found = tracks.find((t) => t.id === e.target.value)
            if (found) onSelectTarget(found.id)
          }}
        >
          <option value="">— не выбрана —</option>
          {tracks
            .filter((t) => t.id !== ownship.id)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.callsign} · {t.source} · {fmtAlt(t.altM)}
              </option>
            ))}
        </select>
      </div>

      {/* Показания инструмента */}
      {solution && target ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Metric
              label="Дальность"
              value={fmtDistance(solution.range)}
              icon={Move3D}
              state="active"
              title="Горизонтальное расстояние по большому кругу"
            />
            <Metric label="Наклонная дальность" value={fmtDistance(solution.slant)} icon={Radar} />
            <Metric label="Азимут" value={fmtBearing(solution.brg)} icon={Navigation} />
            <Metric label="Скорость цели" value={fmtSpeed(target.speedMs)} icon={Gauge} />
            <Metric label="Высота цели" value={fmtAlt(target.altM)} icon={Gauge} />
            <Metric
              label="Превышение"
              value={solution.altDiff !== null ? fmtAlt(solution.altDiff) : '—'}
              icon={Move3D}
              state={
                solution.altDiff === null
                  ? 'pending'
                  : Math.abs(solution.altDiff) < 60
                    ? 'warning'
                    : 'active'
              }
              title="Высота цели минус высота своего БВС. Малый запас — риск конфликта."
            />
          </div>

          {/* Признак сближения по вертикали */}
          <div
            className={`rounded-md border px-2 py-1.5 text-[11px] ut-stated ${
              solution.altDiff !== null && Math.abs(solution.altDiff) < 60
                ? 'ut-state-warning'
                : 'ut-state-approved'
            }`}
          >
            {solution.altDiff === null ? (
              'Высота одной из целей неизвестна — вертикальное сближение не оценивается.'
            ) : solution.altDiff >= 0 ? (
              <>
                Цель <b>выше</b> своего БВС на {fmtAlt(solution.altDiff)}. Расчётная высота
                встречи — {fmtAlt(ownship.altM !== null ? ownship.altM + solution.altDiff : 0)}.
              </>
            ) : (
              <>
                Цель <b>ниже</b> своего БВС на {fmtAlt(Math.abs(solution.altDiff))}. Следите за
                вертикальным сближением.
              </>
            )}
          </div>
        </>
      ) : (
        <EmptyHint>Выберите цель для расчёта дальности и азимута.</EmptyHint>
      )}
    </div>
  )
}
